import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import useMasterMaps, { selCls, inputCls } from "../hooks/useMasterMaps";
import { toast } from "sonner";
import { AlertCircle, Users } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { RatingStars, SourceChip, AppStatusBadge } from "../components/PipelineBits";

const VERTICALS = ["Sales", "Service", "Support"];
const PRIORITIES = ["Critical", "High", "Normal"];

function AppCard({ a, onClick, draggable, onDragStart }) {
  return (
    <div data-testid={`app-card-${a.application_id}`} draggable={draggable} onDragStart={onDragStart} onClick={onClick}
      className={`bg-white border rounded-sm p-3 mb-2 cursor-pointer hover:shadow-sm transition-shadow duration-200 ${a.status === "On Hold" ? "border-gray-400 opacity-70" : "border-gray-200"}`}>
      <div className="flex items-start justify-between gap-1">
        <div className="text-sm font-medium text-[#0E2240] leading-tight">{a.candidate_name}</div>
        {a.feedback_pending && (
          <span data-testid={`feedback-flag-${a.application_id}`} title="Interview feedback pending — clock on BUSINESS"
            className="shrink-0 text-[#BB162B]"><AlertCircle size={14} /></span>
        )}
      </div>
      <div className="text-[10px] text-[#6B7280] mt-0.5">{a.desig_name} · {a.branch_name}</div>
      <div className="text-[10px] text-[#6B7280] tabular-nums">{a.req_no}</div>
      <div className="mt-2 flex items-center justify-between">
        <SourceChip source={a.source} />
        <span className={`text-[10px] font-semibold tabular-nums ${a.days_in_stage >= 7 ? "text-[#BB162B]" : a.days_in_stage >= 3 ? "text-[#D97706]" : "text-[#6B7280]"}`}>
          {a.days_in_stage}d
        </span>
      </div>
      <div className="mt-1 flex items-center justify-between">
        <RatingStars value={a.overall_rating} />
        {a.status === "On Hold" && <span className="text-[9px] uppercase text-[#6B7280]">On Hold</span>}
        {a.priority === "Critical" && <span className="text-[9px] uppercase text-[#BB162B] font-semibold">Critical</span>}
      </div>
    </div>
  );
}

export default function PipelineBoard() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const maps = useMasterMaps();
  const [stages, setStages] = useState([]);
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ branch_id: "", vertical: "", priority: "", owner_id: "", ageing_min: "" });
  const [swimlanes, setSwimlanes] = useState(false);
  const [detail, setDetail] = useState(null);
  const [dragged, setDragged] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    const params = {};
    Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get("/pipeline", { params }).then(({ data }) => { setStages(data.stages); setRows(data.rows); })
      .catch((e) => toast.error(apiError(e)));
  }, [f]);
  useEffect(load, [load]);

  const owners = useMemo(() => {
    const m = {};
    rows.forEach((r) => { if (r.owner_id) m[r.owner_id] = r.owner_name; });
    return Object.entries(m);
  }, [rows]);

  const openDetail = (a) => api.get(`/applications/${a.application_id}`)
    .then(({ data }) => setDetail(data)).catch((e) => toast.error(apiError(e)));

  const drop = async (stage) => {
    if (!dragged || dragged.current_stage === stage) return;
    try {
      await api.post(`/applications/${dragged.application_id}/move`, { to_stage: stage });
      toast.success(`${dragged.candidate_name} → ${stage}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
    setDragged(null);
  };

  const setStatus = async (status) => {
    let reason = "";
    if (status === "Rejected" || status === "Dropped") {
      reason = window.prompt(`${status === "Rejected" ? "Rejection" : "Drop"} reason (mandatory):`) || "";
      if (!reason.trim()) return;
    }
    setBusy(true);
    try {
      await api.post(`/applications/${detail.application.application_id}/status`, { status, reason });
      toast.success(`Application ${status === "Active" ? "reinstated" : status.toLowerCase()}`);
      setDetail(null); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const markJoined = async () => {
    const d = window.prompt("Joining date (YYYY-MM-DD):", new Date().toISOString().slice(0, 10));
    if (!d) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/applications/${detail.application.application_id}/joined`, { joined_on: d });
      toast.success(`Joined — employee ${data.employee_code} created, requisition updated`);
      setDetail(null); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const board = (subset, keySuffix = "") => (
    <div className="flex gap-3 overflow-x-auto pb-3" data-testid={`pipeline-board${keySuffix}`}>
      {stages.map((s) => {
        const items = subset.filter((r) => r.current_stage === s);
        return (
          <div key={s} data-testid={`stage-col-${s.replace(/\s/g, "-")}${keySuffix}`}
            onDragOver={(e) => e.preventDefault()} onDrop={() => drop(s)}
            className="w-56 shrink-0 bg-[#F0F1F4] border border-gray-200 rounded-sm p-2">
            <div className="flex items-center justify-between px-1 pb-2">
              <span className="text-[10px] uppercase tracking-wider font-semibold text-[#0E2240]">{s}</span>
              <span className="text-[10px] tabular-nums bg-white border border-gray-200 rounded-sm px-1.5">{items.length}</span>
            </div>
            {items.map((a) => (
              <AppCard key={a.application_id} a={a} onClick={() => openDetail(a)}
                draggable={can("pipeline", "write")} onDragStart={() => setDragged(a)} />
            ))}
          </div>
        );
      })}
    </div>
  );

  const app = detail?.application;
  return (
    <div data-testid="pipeline-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
        <div>
          <h1 className="font-serif text-2xl sm:text-3xl text-[#0E2240] tracking-tight">Recruitment Pipeline</h1>
          <p className="text-sm text-[#6B7280] mt-1">Drag candidates across stages — feedback gates and offer stages are enforced.</p>
        </div>
        <label className="flex items-center gap-2 text-xs text-[#6B7280] self-start">
          <input data-testid="swimlane-toggle" type="checkbox" checked={swimlanes} onChange={(e) => setSwimlanes(e.target.checked)} />
          Swimlanes by recruiter
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="pipeline-filters">
        <select data-testid="pl-branch-filter" className={selCls} value={f.branch_id} onChange={(e) => setF((p) => ({ ...p, branch_id: e.target.value }))}>
          <option value="">All branches</option>
          {maps.branches.map((b) => <option key={b.branch_id} value={b.branch_id}>{b.branch_name}</option>)}
        </select>
        <select data-testid="pl-vertical-filter" className={selCls} value={f.vertical} onChange={(e) => setF((p) => ({ ...p, vertical: e.target.value }))}>
          <option value="">All verticals</option>
          {VERTICALS.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <select data-testid="pl-priority-filter" className={selCls} value={f.priority} onChange={(e) => setF((p) => ({ ...p, priority: e.target.value }))}>
          <option value="">All priorities</option>
          {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select data-testid="pl-owner-filter" className={selCls} value={f.owner_id} onChange={(e) => setF((p) => ({ ...p, owner_id: e.target.value }))}>
          <option value="">All recruiters</option>
          {owners.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </select>
        <input data-testid="pl-ageing-filter" type="number" min="0" className={`${inputCls} !w-32`} placeholder="Ageing ≥ days"
          value={f.ageing_min} onChange={(e) => setF((p) => ({ ...p, ageing_min: e.target.value }))} />
      </div>

      {swimlanes ? (
        owners.map(([id, name]) => (
          <div key={id} className="mb-6">
            <div className="flex items-center gap-2 mb-2 text-sm font-medium text-[#0E2240]"><Users size={14} /> {name}</div>
            {board(rows.filter((r) => r.owner_id === id), `-${id}`)}
          </div>
        ))
      ) : board(rows)}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        {detail && (
          <DialogContent className="bg-white rounded-sm shadow-none border border-gray-200 max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="application-detail-dialog">
            <DialogHeader>
              <DialogTitle className="font-serif text-[#0E2240] flex items-center gap-2">
                {app.candidate_name} <AppStatusBadge status={app.status} />
              </DialogTitle>
              <DialogDescription className="text-xs text-[#6B7280]">
                {app.desig_name} at {app.branch_name} · {app.req_no} · with {app.owner_name || "—"} · {app.current_stage} for {app.days_in_stage}d
              </DialogDescription>
            </DialogHeader>
            {app.feedback_pending && (
              <div className="text-xs text-[#BB162B] border border-[#BB162B]/40 bg-[#BB162B]/5 rounded-sm p-3">
                Interview feedback pending in the system — candidate is blocked from moving forward. This day count sits with BUSINESS.
              </div>
            )}
            {(app.rejection_reason || app.drop_reason) && (
              <div className="text-xs text-[#6B7280] border border-gray-200 rounded-sm p-3">{app.rejection_reason || app.drop_reason}</div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <div className="text-xs uppercase tracking-wider text-[#6B7280] mb-2">Stage history</div>
                <div className="space-y-1.5 max-h-56 overflow-y-auto" data-testid="stage-history">
                  {detail.stage_log.map((l) => (
                    <div key={l.log_id} className="text-xs border-l-2 border-[#BF9B30]/50 pl-2">
                      <span className="text-[#0E2240]">{l.from_stage ? `${l.from_stage} → ` : ""}{l.to_stage}</span>
                      <div className="text-[10px] text-[#6B7280]">{l.by_name} · {String(l.on).slice(0, 16).replace("T", " ")}{l.note && ` · ${l.note}`}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-[#6B7280] mb-2">Interviews</div>
                {detail.interviews.length === 0 ? <div className="text-xs text-[#6B7280]">None scheduled.</div> :
                  detail.interviews.map((iv) => (
                    <div key={iv.interview_id} className="border border-gray-200 rounded-sm p-2 mb-1.5 text-xs">
                      <div className="flex justify-between">
                        <span className="font-medium text-[#0E2240]">R{iv.round_no} · {iv.interview_mode}</span>
                        <span className={iv.feedback_submitted_on ? "text-[#1B7F3B]" : "text-[#BB162B]"}>
                          {iv.outcome || (iv.feedback_submitted_on ? "Done" : "Feedback pending")}
                        </span>
                      </div>
                      <div className="text-[10px] text-[#6B7280]">{String(iv.scheduled_on).slice(0, 16).replace("T", " ")} · Panel: {iv.panel_names.join(", ")}</div>
                      {iv.feedback_text && <div className="text-[10px] text-[#0E2240] mt-1">"{iv.feedback_text}"</div>}
                    </div>
                  ))}
                {detail.offer && (
                  <>
                    <div className="text-xs uppercase tracking-wider text-[#6B7280] mt-3 mb-2">Offer</div>
                    <div className="border border-gray-200 rounded-sm p-2 text-xs">
                      ₹{Number(detail.offer.offered_ctc).toLocaleString()} · {detail.offer.offer_released_on ? (detail.offer.candidate_response || "Awaiting response") : detail.offer.approval_status}
                      {detail.offer.proposed_doj && <div className="text-[10px] text-[#6B7280]">DOJ: {detail.offer.revised_doj || detail.offer.proposed_doj}</div>}
                    </div>
                  </>
                )}
              </div>
            </div>
            {can("pipeline", "write") && (
              <DialogFooter className="flex-wrap gap-2">
                {app.status === "Active" && ["Screening", "Shortlisted", "Interview L1", "Interview L2"].includes(app.current_stage) && (
                  <button data-testid="detail-schedule-interview" onClick={() => navigate(`/interviews?app=${app.application_id}`)}
                    className="px-3 py-2 text-xs bg-[#1E5AA8] text-white rounded-sm hover:bg-[#17497f]">Schedule interview</button>
                )}
                {app.status === "Active" && app.current_stage === "Selected" && can("offers", "write") && (
                  <button data-testid="detail-create-offer" onClick={() => navigate(`/offers?app=${app.application_id}`)}
                    className="px-3 py-2 text-xs bg-[#1E5AA8] text-white rounded-sm hover:bg-[#17497f]">Create offer</button>
                )}
                {app.status === "Active" && ["Offer Accepted", "Documentation"].includes(app.current_stage) && (
                  <button data-testid="detail-mark-joined" disabled={busy} onClick={markJoined}
                    className="px-3 py-2 text-xs bg-[#1B7F3B] text-white rounded-sm hover:bg-[#166832] disabled:opacity-50">Mark joined</button>
                )}
                {app.status === "Active" && (
                  <>
                    <button data-testid="detail-app-reject" disabled={busy} onClick={() => setStatus("Rejected")}
                      className="px-3 py-2 text-xs border border-[#BB162B]/60 text-[#BB162B] rounded-sm hover:bg-[#BB162B]/5 disabled:opacity-50">Reject</button>
                    <button data-testid="detail-app-drop" disabled={busy} onClick={() => setStatus("Dropped")}
                      className="px-3 py-2 text-xs border border-[#D97706]/60 text-[#D97706] rounded-sm hover:bg-[#D97706]/5 disabled:opacity-50">Candidate dropped</button>
                    <button data-testid="detail-app-hold" disabled={busy} onClick={() => setStatus("On Hold")}
                      className="px-3 py-2 text-xs border border-gray-300 text-[#6B7280] rounded-sm hover:bg-gray-50 disabled:opacity-50">Hold</button>
                  </>
                )}
                {app.status === "On Hold" && (
                  <button data-testid="detail-app-reinstate" disabled={busy} onClick={() => setStatus("Active")}
                    className="px-3 py-2 text-xs bg-[#0E2240] text-white rounded-sm hover:bg-[#1a3358] disabled:opacity-50">Reinstate</button>
                )}
              </DialogFooter>
            )}
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
